import React, { useState, useEffect } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MoreHorizontal, Search, PackageX, TrendingDown, Package } from "lucide-react";

function StatusPill({ status }) {
    const map = {
        in_stock: { label: "In stock", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
        low_stock: { label: "Low stock", cls: "bg-amber-50 text-amber-700 border-amber-200" },
        out_of_stock: { label: "Out of stock", cls: "bg-red-50 text-red-700 border-red-200" },
    };
    const s = map[status] || map.in_stock;
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${s.cls}`}>
            {s.label}
        </span>
    );
}

export default function InventoryIntelligence() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [adjust, setAdjust] = useState(null);
    const [levels, setLevels] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/inventory", { params: q ? { q } : {} });
            setItems(data.items || []);
            setSummary(data.summary || null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [q]);

    const summaryCards = summary
        ? [
              { key: "total", label: "SKUs tracked", value: summary.total_products, icon: Package },
              {
                  key: "value",
                  label: "Stock value",
                  value: `₹${Number(summary.total_value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
              },
              { key: "low", label: "Low stock", value: summary.low_stock, icon: TrendingDown, tone: "amber" },
              { key: "out", label: "Out of stock", value: summary.out_of_stock, icon: PackageX, tone: "red" },
          ]
        : [];

    return (
        <div data-testid="page-inventory-intelligence">
            <PageHeader
                title="Inventory Intelligence"
                description="Predictive stock signals to prevent stockouts and over-purchase. Add products in Product Brain to populate this view."
            />
            <div className="p-8 space-y-4 max-w-[1400px]">
                {summary && (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        {summaryCards.map((c) => (
                            <div
                                key={c.key}
                                data-testid={`inv-summary-${c.key}`}
                                className={`rounded-md border bg-white px-4 py-3 ${
                                    c.tone === "amber"
                                        ? "border-amber-200"
                                        : c.tone === "red"
                                          ? "border-red-200"
                                          : "border-zinc-200"
                                }`}
                            >
                                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                                    {c.label}
                                </div>
                                <div className="mt-1 font-display text-xl font-semibold">{c.value}</div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <div className="relative flex-1 max-w-xs">
                        <Search
                            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400"
                            strokeWidth={1.5}
                        />
                        <Input
                            placeholder="Search SKU, name, brand…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-8 h-9"
                            data-testid="inv-search"
                        />
                    </div>
                    <div className="ml-auto text-xs text-zinc-500 font-mono">{items.length} items</div>
                </div>

                <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-zinc-50/60">
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Product</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Warehouse</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Qty</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Reorder</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Value</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Status</TableHead>
                                <TableHead className="w-10" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="py-12 text-center text-sm text-zinc-500">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="py-12 text-center text-sm text-zinc-500">
                                        No inventory yet. Add products in{" "}
                                        <a href="/app/product-brain" className="underline">
                                            Product Brain
                                        </a>{" "}
                                        to start tracking.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((row) => (
                                    <TableRow key={row.id} data-testid={`inv-row-${row.id}`}>
                                        <TableCell>
                                            <div className="text-sm font-medium text-zinc-900">{row.product.name}</div>
                                            <div className="text-xs text-zinc-500 font-mono">
                                                {row.product.sku}
                                                {row.product.brand && ` · ${row.product.brand}`}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="text-[10px] uppercase tracking-widest">
                                                {row.warehouse}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs">
                                            {row.quantity_on_hand} {row.product.unit}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs text-zinc-500">
                                            {row.reorder_level}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs">
                                            ₹{Number(row.value).toLocaleString("en-IN")}
                                        </TableCell>
                                        <TableCell>
                                            <StatusPill status={row.status} />
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        data-testid={`inv-actions-${row.id}`}
                                                    >
                                                        <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => setAdjust(row)}>
                                                        Adjust stock
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => setLevels(row)}>
                                                        Set thresholds
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <AdjustDialog record={adjust} onClose={() => setAdjust(null)} onDone={load} />
            <LevelsDialog record={levels} onClose={() => setLevels(null)} onDone={load} />
        </div>
    );
}

function AdjustDialog({ record, onClose, onDone }) {
    const [mode, setMode] = useState("receipt");
    const [qty, setQty] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (record) {
            setMode("receipt");
            setQty("");
        }
    }, [record]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const n = Number(qty);
        const delta = mode === "receipt" ? n : -n;
        try {
            await api.post(`/inventory/${record.id}/adjust`, { delta, reason: mode });
            toast.success("Stock updated");
            onDone();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!record} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="inv-adjust-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Adjust stock</DialogTitle>
                    <DialogDescription>{record?.product?.name}</DialogDescription>
                </DialogHeader>
                {record && (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600 font-mono">
                            Current: {record.quantity_on_hand} {record.product.unit}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Movement</Label>
                                <Select value={mode} onValueChange={setMode}>
                                    <SelectTrigger data-testid="inv-adjust-mode">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="receipt">Receipt (+)</SelectItem>
                                        <SelectItem value="issue">Issue (−)</SelectItem>
                                        <SelectItem value="correction">Correction</SelectItem>
                                        <SelectItem value="count">Physical count</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Quantity</Label>
                                <Input
                                    type="number"
                                    step="any"
                                    required
                                    value={qty}
                                    onChange={(e) => setQty(e.target.value)}
                                    data-testid="inv-adjust-qty"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={saving} data-testid="inv-adjust-submit">
                                {saving ? "Saving…" : "Apply"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

function LevelsDialog({ record, onClose, onDone }) {
    const [reorder, setReorder] = useState("");
    const [safe, setSafe] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (record) {
            setReorder(String(record.reorder_level ?? 0));
            setSafe(String(record.safe_stock ?? 0));
        }
    }, [record]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch(`/inventory/${record.id}/levels`, {
                reorder_level: Number(reorder),
                safe_stock: Number(safe),
            });
            toast.success("Thresholds updated");
            onDone();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!record} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="inv-levels-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Set stock thresholds</DialogTitle>
                    <DialogDescription>{record?.product?.name}</DialogDescription>
                </DialogHeader>
                {record && (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Reorder at</Label>
                                <Input type="number" value={reorder} onChange={(e) => setReorder(e.target.value)} data-testid="inv-reorder" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Safe stock</Label>
                                <Input type="number" value={safe} onChange={(e) => setSafe(e.target.value)} data-testid="inv-safe" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={saving} data-testid="inv-levels-submit">
                                {saving ? "Saving…" : "Save"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
